<template>
  <BaseModal
    :open="!templateLibraryOpen && !globalStore.appUpgradePlanModal"
    :has-divider="true"
    class="print-diet-modal"
    description="Dostosuj wygląd i zawartość wydruku PDF dla pacjenta."
    :style="{ '--ui-modal-max-width': '1024px' }"
    title="Pobierz PDF"
    @close="hidePrintDietModal"
  >
    <template #icon>
      <FeaturedIcon background-color="color-base-white" name="FileDownload03" />
    </template>

    <div class="print-diet-modal__grid" @click.capture="checkPermissions">
      <PrintLayoutSettings
        :day-format="dayFormat"
        :enabled="printConfig"
        :options="print"
        :themes="printThemes"
        @day-format="dayFormat = $event"
        @group-by="setGroupBy"
        @option="updatePrintOption"
        @preview="openTemplateLibrary"
      />
      <PrintContentOptions
        :can-disable-footer="hasPerm('accounts.print_disable_footer')"
        :enabled="printConfig"
        :losses-active="lossesActive"
        :options="print"
        @option="updatePrintOption"
      />
    </div>

    <div v-if="testPrint" class="test-print-warning">
      Wydruk jadłospisu PDF jest niedostępny w ramach 14-dniowego darmowego okresu próbnego. Zobacz
      wydruk przykładowy, a następnie wykup pakiet.
    </div>

    <template #footer>
      <PrintDietFooter
        :limited="isLimitedPrintVersion"
        :limit-exceeded="printLimitExceeded"
        :remaining="printDietLimit"
        :test-print="testPrint"
        @close="hidePrintDietModal"
        @download="handleDownloadClick"
        @sample="printDietModalConfirm"
        @upgrade="redirectToPlanChange"
      />
    </template>
  </BaseModal>
  <PrintTemplateLibrary
    v-if="templateLibraryOpen"
    :patient-id="dietitianPatient?.id"
    :value="print.theme"
    @close="templateLibraryOpen = false"
    @select="setTheme"
  />
</template>

<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, ref, toRef } from 'vue'

  import { BaseModal, FeaturedIcon } from '@/components/ui'
  import { getScheduleDayHeader } from '@/const'
  import { authService } from '@/services/authService'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { byDefault } from '@/utils/byDefault'

  import type { DietType } from '@/store/types'

  import { buildPrintUrl } from './buildPrintUrl'
  import PrintContentOptions from './components/PrintContentOptions/PrintContentOptions.vue'
  import PrintDietFooter from './components/PrintDietFooter/PrintDietFooter.vue'
  import PrintLayoutSettings from './components/PrintLayoutSettings/PrintLayoutSettings.vue'
  import PrintTemplateLibrary from './components/PrintTemplateLibrary/PrintTemplateLibrary.vue'
  import { getPrintThemes } from './printThemes'
  import type { DayFormat, GroupBy, PrintOptions } from './types'

  const props = defineProps<{
    dietId: number
    dietType: DietType
  }>()

  const dietType = toRef(props.dietType)
  const dietId = toRef(props.dietId)

  const globalStore = useGlobalStore()
  const dietStore = useDietsStore()

  const { hasPerm, showAppUpgradePlanModal, incrementPrintedDiets } = globalStore
  const { user } = storeToRefs(globalStore)
  const { diet } = storeToRefs(dietStore)

  const emit = defineEmits<{ close: [] }>()

  const dietitianPatient = computed(() => {
    if (diet.value && 'dietitian_patient' in diet.value) {
      return diet.value.dietitian_patient
    }

    return undefined
  })

  const initialGroupBy: GroupBy =
    dietitianPatient.value?.pdf_group_by === 'meals' ? 'meals' : 'days'
  const initialSortBy: 'alphabetically' | 'grams' =
    dietitianPatient.value?.pdf_sort_by === 'alphabetically' ? 'alphabetically' : 'grams'
  const dayFormat = ref<DayFormat>(
    dietitianPatient.value?.diet_type ?? diet.value?.data.diet_type ?? 'days_of_week',
  )
  const testPrint = ref(false)
  const templateLibraryOpen = ref(false)
  const weeklyShoppingListsDefault = byDefault(dietitianPatient.value?.pdf_show_shopping_list, true)
  const lossesActive = computed(() => Boolean(diet.value?.data.inevitable_losses.active))
  const hasAssignedDate = computed(() =>
    Boolean(getScheduleDayHeader(0, diet.value?.data.start_date).date),
  )
  const print = ref<PrintOptions>({
    groupBy: initialGroupBy,
    printInevitableLosses: lossesActive.value,
    separateDaysOrMeals: byDefault(dietitianPatient.value?.pdf_separate_days_or_meals, false),
    showChosenNutrients: byDefault(dietitianPatient.value?.pdf_show_chosen_nutrients, false),
    showComments: byDefault(dietitianPatient.value?.pdf_show_comments, true),
    showConsolidatedShoppingList: weeklyShoppingListsDefault,
    showKcalmarLogo: byDefault(dietitianPatient.value?.pdf_show_kcalmar_logo, true),
    showMealHour: byDefault(dietitianPatient.value?.pdf_show_meal_hour, true),
    showNutrients: byDefault(dietitianPatient.value?.pdf_show_nutrients, true),
    showPreparationTime: byDefault(dietitianPatient.value?.pdf_show_preparation_time, true),
    showQRCodes: byDefault(dietitianPatient.value?.pdf_show_qr_codes, true),
    showShoppingList: weeklyShoppingListsDefault,
    showWeek: byDefault(dietitianPatient.value?.pdf_show_week, true),
    sortBy: initialSortBy,
    theme: byDefault(dietitianPatient.value?.theme, 'kcalmar'),
  })

  // Weekly shopping lists only exist when grouping by days.
  const applyGroupRules = (groupBy: GroupBy) => {
    print.value.showShoppingList = groupBy === 'days' && weeklyShoppingListsDefault
    if (groupBy === 'meals') print.value.showWeek = false
  }

  const setGroupBy = (groupBy: GroupBy) => {
    print.value.groupBy = groupBy
    applyGroupRules(groupBy)
  }

  applyGroupRules(initialGroupBy)
  if (print.value.showNutrients) print.value.showChosenNutrients = false

  const printConfig = computed(() => hasPerm('accounts.print_config'))
  const isLimitedPrintVersion = computed(() => {
    return (
      (user?.value?.permissions.includes('accounts.print_limited') &&
        !user.value.permissions.includes('accounts.print_full')) ??
      false
    )
  })
  const printDietLimit = computed(() => {
    const printedDiets = user?.value?.printed_diets ?? import.meta.env.VUE_APP_PRINTED_DIET_LIMIT
    return import.meta.env.VUE_APP_PRINTED_DIET_LIMIT - printedDiets
  })
  const printLimitExceeded = computed(
    () => isLimitedPrintVersion.value && printDietLimit.value <= 0,
  )

  const printThemes = computed(() => getPrintThemes(hasPerm))

  const setTheme = (theme: number | string | null) => {
    if (typeof theme === 'string') print.value.theme = theme
  }

  const openTemplateLibrary = () => {
    templateLibraryOpen.value = true
  }

  const updatePrintOption = <Key extends keyof PrintOptions>(
    key: Key,
    value: PrintOptions[Key],
  ) => {
    print.value[key] = value
    if (key === 'showNutrients' && value === true) {
      print.value.showChosenNutrients = false
    }
    if (key === 'showChosenNutrients' && value === true) {
      print.value.showNutrients = false
    }
  }

  const checkPermissions = (event: Event) => {
    const target = event.target
    const isTemplateControl =
      target instanceof Element && target.closest('.print-diet-modal__template-control')

    if (!printConfig.value && !isTemplateControl) {
      event.stopPropagation()
      showAppUpgradePlanModal()
    }
  }

  const redirectToPlanChange = () => {
    hidePrintDietModal()
    window.open(import.meta.env.VUE_APP_BASE_CLIENT_CREATOR_URL, '_blank')
  }

  const hidePrintDietModal = () => {
    emit('close')
    testPrint.value = false
    templateLibraryOpen.value = false
  }

  const handleDownloadClick = () => {
    if (
      !hasPerm('accounts.print_test') ||
      hasPerm('accounts.print_full') ||
      isLimitedPrintVersion.value
    ) {
      printDietModalConfirm()
    } else {
      testPrint.value = true
    }
  }

  const printDietModalConfirm = () => {
    const url = buildPrintUrl({
      dayFormat: dayFormat.value,
      dietId: dietId.value,
      dietType: dietType.value,
      hasAssignedDate: hasAssignedDate.value,
      hasFullPrint: hasPerm('accounts.print_full'),
      hasLimitedPrint: hasPerm('accounts.print_limited'),
      host: import.meta.env.VUE_APP_HOST,
      options: print.value,
      systemDietId: import.meta.env.VUE_APP_SYSTEM_DIET_ID,
      testPrint: hasPerm('accounts.print_test'),
      token: authService.getAccessToken() ?? '',
    })
    window.open(url, '_blank')
    incrementPrintedDiets()
    hidePrintDietModal()
  }

  onMounted(() => {
    if (!printThemes.value.some(theme => theme.id === print.value.theme)) {
      print.value.theme = printThemes.value[0]?.id ?? 'kcalmar'
    }
  })
</script>

<style lang="scss">
  @use '@repo/ui/typography.scss' as typography;

  .print-diet-modal .ui-modal-content {
    padding: 0 36px;
  }

  .print-diet-modal__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 24px 0;
    gap: 64px;
  }

  .print-diet-modal__row {
    display: flex;
    padding: 12px 0;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-border-border-secondary);
  }

  .print-diet-modal__settings .ui-button[data-ui-state='pressed']:not(:disabled) {
    background: var(--color-background-bg-quaternary);
  }

  .print-diet-modal__template-control {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .test-print-warning {
    @include typography.font-style('text-sm', 'regular');

    padding: 12px;
    margin-top: 20px;
    color: var(--color-text-text-error-primary-600);
    background: var(--color-background-bg-error-secondary);
    border-radius: var(--radius-md);
  }

  .print-diet-modal__footer {
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: space-between;
  }

  .print-diet-modal__limit {
    @include typography.font-style('text-xs', 'regular');

    max-width: 300px;
    color: var(--color-text-text-secondary-700);
  }

  .print-diet-modal__limit p {
    margin: 0;
  }

  .print-diet-modal__limit-warning {
    color: var(--color-text-text-error-primary-600);
  }

  .print-diet-modal__actions {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
  }
</style>
